import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { socket } from '../socket';
import { Users, LogIn, Plus, List, Lock, Globe } from 'lucide-react';
import { BrandLogo } from '../components/BrandLogo';
import './Lobby.css';
import { gameBrand } from '../config/brand';

export default function Lobby() {
  const [name, setName] = useState('');
  const [roomId, setRoomId] = useState('');
  const [password, setPassword] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'create', 'join'
  const [publicRooms, setPublicRooms] = useState([]);
  const [error, setError] = useState('');
  const [isLoadingRooms, setIsLoadingRooms] = useState(false);
  const [isConnected, setIsConnected] = useState(socket.connected);
  const navigate = useNavigate();
  const [reconnectInfo, setReconnectInfo] = useState(null);

  useEffect(() => {
    const lastActiveRoomId = localStorage.getItem('lastActiveRoomId');
    const playerId = localStorage.getItem('playerId');
    const reconnectToken = localStorage.getItem('reconnectToken');

    if (lastActiveRoomId && playerId && reconnectToken) {
      fetch(`/api/check-reconnect?roomId=${lastActiveRoomId}&playerId=${playerId}&reconnectToken=${reconnectToken}`)
        .then(res => res.json())
        .then(data => {
          if (data.reconnectAvailable) {
            setReconnectInfo({
              roomId: lastActiveRoomId,
              hostName: data.hostName
            });
          }
        })
        .catch(err => console.error('Failed to check reconnect status', err));
    }
  }, []);

  useEffect(() => {
    const savedName = localStorage.getItem('playerName');
    if (savedName) setName(savedName);

    const searchParams = new URLSearchParams(window.location.search);
    const roomParam = searchParams.get('room');
    if (roomParam) {
      setRoomId(roomParam.toUpperCase());
      setActiveTab('join');
    }

    socket.connect();

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    socket.on('roomCreated', ({ roomId, playerId, reconnectToken }) => {
      localStorage.setItem('playerId', playerId);
      localStorage.setItem('reconnectToken', reconnectToken);
      navigate(`/room/${roomId}`);
    });

    socket.on('roomJoined', ({ roomId, playerId, reconnectToken }) => {
      localStorage.setItem('playerId', playerId);
      localStorage.setItem('reconnectToken', reconnectToken);
      navigate(`/room/${roomId}`);
    });

    socket.on('error', (err) => {
      setError(err.message);
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connection error:', err);
      setIsConnected(false);
      setError('Sunucu bağlantı hatası. Lütfen backend sunucusunun çalıştığından emin olun.');
    });

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('roomCreated');
      socket.off('roomJoined');
      socket.off('error');
      socket.off('connect_error');
    };
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'list') {
      fetchPublicRooms();
      const interval = setInterval(fetchPublicRooms, 5000); // Poll every 5s
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const fetchPublicRooms = async () => {
    setIsLoadingRooms(true);
    try {
      const response = await fetch('/api/rooms');
      const data = await response.json();
      setPublicRooms(data);
    } catch (err) {
      console.error('Failed to fetch rooms', err);
    }
    setIsLoadingRooms(false);
  };

  const handleCreateRoom = (e) => {
    e.preventDefault();
    if (!isConnected) {
      setError('Sunucuya bağlı değilsiniz. Lütfen bağlantının kurulmasını bekleyin.');
      socket.connect();
      return;
    }
    if (!name.trim()) {
      setError('Lütfen bir isim girin');
      return;
    }
    const playerId = localStorage.getItem('playerId');
    const reconnectToken = localStorage.getItem('reconnectToken');

    localStorage.setItem('playerName', name.trim());
    socket.emit('createRoom', { 
      playerName: name.trim(), 
      avatar: 'default',
      playerId,
      reconnectToken,
      isPublic: isPublic,
      password: isPublic ? '' : password
    });
  };

  const handleJoinRoom = (e) => {
    e.preventDefault();
    if (!isConnected) {
      setError('Sunucuya bağlı değilsiniz. Lütfen bağlantının kurulmasını bekleyin.');
      socket.connect();
      return;
    }
    if (!name.trim() || !roomId.trim()) {
      setError('Lütfen isim ve oda kodunu girin');
      return;
    }
    const playerId = localStorage.getItem('playerId');
    const reconnectToken = localStorage.getItem('reconnectToken');

    localStorage.setItem('playerName', name.trim());
    socket.emit('joinRoom', { 
      roomId: roomId.toUpperCase(), 
      playerName: name.trim(), 
      avatar: 'default',
      playerId,
      reconnectToken,
      password: password
    });
  };

  const handleJoinPublicRoom = (rId) => {
    if (!name.trim()) {
      setError('Lütfen önce oyuncu adı girin');
      return;
    }
    const playerId = localStorage.getItem('playerId');
    const reconnectToken = localStorage.getItem('reconnectToken');

    localStorage.setItem('playerName', name.trim());
    socket.emit('joinRoom', { 
      roomId: rId, 
      playerName: name.trim(), 
      avatar: 'default',
      playerId,
      reconnectToken,
      password: ''
    });
  };

  return (
    <div className="lobby-container">
      <div className="glass-panel">
        <div className="connection-status-bar" style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '0.8rem', color: isConnected ? '#10b981' : '#ef4444', gap: '6px', alignItems: 'center', marginBottom: '-10px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isConnected ? '#10b981' : '#ef4444', display: 'inline-block' }}></span>
          {isConnected ? 'Sunucuya Bağlandı' : 'Bağlantı Aranıyor...'}
        </div>
        <div className="logo-container">
          <BrandLogo size={60} className="logo-icon" />
          <h1>{gameBrand.name} <span>Online</span></h1>
        </div>

        {error && <div className="error-message">{error}</div>}

        {reconnectInfo && (
          <div 
            className="reconnect-banner animate-fade-in"
            style={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(220, 38, 38, 0.15) 100%)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(239, 68, 68, 0.15)',
              backdropFilter: 'blur(8px)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fca5a5' }}>
              <span style={{ fontSize: '1.4rem' }}>⚡</span>
              <span style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Bir önce çıktığın oyun hala devam ediyor!</span>
            </div>
            <button 
              onClick={() => {
                navigate(`/room/${reconnectInfo.roomId}`);
              }}
              style={{
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '0.9rem',
                boxShadow: '0 0 15px rgba(239, 68, 68, 0.5)',
                transition: 'transform 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
               Katılmak İster misin?
            </button>
          </div>
        )}

        <div className="input-group global-name-input">
          <label>Oyuncu Adı</label>
          <input 
            type="text" 
            name="username"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            placeholder="Adınızı girin..." 
            value={name} 
            onChange={(e) => setName(e.target.value)}
            maxLength={15}
          />
        </div>

        <div className="action-tabs">
          <button 
            className={`tab ${activeTab === 'list' ? 'active' : ''}`} 
            onClick={() => setActiveTab('list')}
          >
            <List size={18} /> Açık Odalar
          </button>
          <button 
            className={`tab ${activeTab === 'create' ? 'active' : ''}`} 
            onClick={() => setActiveTab('create')}
          >
            <Plus size={18} /> Oda Kur
          </button>
          <button 
            className={`tab ${activeTab === 'join' ? 'active' : ''}`} 
            onClick={() => setActiveTab('join')}
          >
            <LogIn size={18} /> Özel Oda
          </button>
        </div>

        <div className="form-container">
          {activeTab === 'list' && (
            <div className="public-rooms-list">
              {isLoadingRooms && publicRooms.length === 0 ? (
                <div className="loading-text">Odalar aranıyor...</div>
              ) : publicRooms.length === 0 ? (
                <div className="empty-rooms">Açık oda bulunamadı. Yeni bir oda kurabilirsiniz.</div>
              ) : (
                <div className="rooms-grid">
                  {publicRooms.map(room => (
                    <div className="room-card" key={room.id}>
                      <div className="room-info">
                        <div className="room-name">#{room.id}</div>
                        <div className="room-host">Kurucu: {room.hostName}</div>
                      </div>
                      <div className="room-stats">
                        <Users size={14} /> {room.playerCount}/{room.maxPlayers}
                      </div>
                      <button 
                        className="join-btn pulse-glow"
                        onClick={() => handleJoinPublicRoom(room.id)}
                        disabled={room.playerCount >= room.maxPlayers}
                      >
                        Katıl
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'create' && (
            <>
              <div className="settings-group">
                <label className="switch-label">
                  <Globe size={18} />
                  <span>Public Oda (Listede Gözükür)</span>
                  <input 
                    type="checkbox" 
                    checked={isPublic} 
                    onChange={(e) => setIsPublic(e.target.checked)} 
                  />
                </label>
              </div>

              {!isPublic && (
                <div className="input-group">
                  <label>Oda Şifresi</label>
                  <input 
                    type="text" 
                    placeholder="Şifre belirleyin..." 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)}
                    maxLength={10}
                  />
                </div>
              )}

              <button 
                className="primary-button" 
                onClick={handleCreateRoom}
              >
                <Plus size={20} />
                Yeni Oda Kur
              </button>
            </>
          )}

          {activeTab === 'join' && (
            <>
              <div className="input-group">
                <label>Oda Kodu</label>
                <input 
                  type="text" 
                  placeholder="Örn: X7A9BC" 
                  value={roomId} 
                  onChange={(e) => setRoomId(e.target.value.toUpperCase())}
                  maxLength={6}
                  className="uppercase-input"
                />
              </div>

              <div className="input-group">
                <label>Oda Şifresi (Varsa)</label>
                <input 
                  type="text" 
                  placeholder="Şifre" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  maxLength={10}
                />
              </div>

              <button 
                className="primary-button" 
                onClick={handleJoinRoom}
              >
                <Lock size={20} />
                Odaya Katıl
              </button>
            </>
          )}
        </div>
      </div>
      
      <div className="background-elements">
        <div className="cube cube-1"></div>
        <div className="cube cube-2"></div>
        <div className="cube cube-3"></div>
      </div>
    </div>
  );
}
